import { slideFromTop } from '@/components/features/casino/crash/GameBox/PreviourRoundResult/styles';
import WrappedButton from '@/components/ui/WrappedButton';
import WrappedModal from '@/components/ui/WrappedModal';
import WrappedTabs from '@/components/ui/WrappedTab';
import LocalizedLink from '@/components/ui/LocalizedLink';
import useReferralCode from '@/hooks/biz/useReferralCode';
import useUpdateUserInfoAsync from '@/hooks/biz/useUpdateUserInfoAsync';
import useUserLoginLogout from '@/hooks/biz/useUserLoginLogout';
import {
  GLOBAL_MODALS,
  globalModals,
  wowSapceAppSidebarMobileOpenState,
  appSidebarOpenState,
} from '@/state/globalSettings';
import { isLoggedIn } from '@/state/userInfo';
import useTheme from '@/theme/useTheme';
import { Box, Grid, IconButton, useMediaQuery } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { memo, useState } from 'react';
// import registerBanner from './register_banner.png';
import registerBanner from './desktop_login_register_banner.png';
import loginBannerMobile from './mobile_login_banner.png';
import registerBannerMobile from './mobile_register_banner.png';
import Loading from '@/components/ui/Loading';
import { getStaticAsset } from '@/utils/staticAsset';
import useBindEmailOrLogin from '@/hooks/useBindEmailOrLogin';
import { isWoWSpaceView } from '@/utils/checkRuntime';
import useResponsiveUI from '@/hooks/useResponsiveUI';
import { Menu } from 'lucide-react';

const UserDropdown = dynamic(() => import('./useUserDropdown'), {
  ssr: false,
});

const LoginForm = dynamic(() => import('./LoginForm'), {
  ssr: false,
  loading: () => <Loading size={120} />,
});

const RegisterForm = dynamic(() => import('./RegisterForm'), {
  ssr: false,
  loading: () => <Loading size={120} />,
});

const ResetPasswordForm = dynamic(() => import('./ResetPassword'), {
  ssr: false,
  loading: () => <Loading size={120} />,
});

declare const window: Window &
  typeof globalThis & {
    Telegram: any;
  };

const UserWidget: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { login } = useUserLoginLogout();

  const isLogin = useAtomValue(isLoggedIn);
  const { isTemporaryLogin } = useBindEmailOrLogin();
  const { isMobile } = useResponsiveUI();

  const [sidebarMobileOpenState, setSidebarMobileOpenState] = useAtom(
    isWoWSpaceView ? wowSapceAppSidebarMobileOpenState : appSidebarOpenState,
  );

  const handleSideBarDrawerToggle = () => {
    setSidebarMobileOpenState(!sidebarMobileOpenState);
  };

  return (
    <Grid display={'flex'} alignItems={'center'} gap={2}>
      <Grid
        display={'flex'}
        gap={2}
        alignItems={'center'}
        sx={{
          display: (isLogin || isTemporaryLogin) && !isMobile ? 'flex' : 'none',
        }}
      >
        <UserDropdown />
      </Grid>
      <Grid
        display={'flex'}
        gap={1}
        sx={{
          animation: `${slideFromTop} 0.5s ease-in-out`,
          display: isLogin || isTemporaryLogin ? 'none' : 'flex',
        }}
      >
        <LocalizedLink href="/account/login">
          <WrappedButton
            variant={'plain'}
            color={'primary'}
            sx={{
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                background: theme.palette.grey['800'],
                boxShadow: theme.palette.boxShadow.elevation7,
                filter: 'none',
              },
            }}
          >
            {t('Login')}
          </WrappedButton>
        </LocalizedLink>
        <LocalizedLink href="/account/register">
          <WrappedButton
            variant={'contained'}
            sx={{
              color: theme.palette.text.white,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                background: theme.palette.primary.dark,
                boxShadow: theme.palette.boxShadow.elevation7,
              },
            }}
          >
            {t('Register')}
          </WrappedButton>
        </LocalizedLink>
      </Grid>
      {isLogin && (
        <IconButton
          onClick={handleSideBarDrawerToggle}
          sx={{
            display: { xs: 'flex', sm: 'none' },
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.grey['900'],
            border: `1px solid ${theme.palette.grey['800']}`,
            '&:hover': {
              backgroundColor: theme.palette.grey['800'],
            },
            width: '40px',
            height: '40px',
          }}
        >
          <Menu size={20} />
        </IconButton>
      )}
    </Grid>
  );
};

export default memo(UserWidget);
